import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import { toast } from 'sonner';
import {
  Dumbbell, Apple, Scale, Pill, BarChart3, MessageSquare, Send, Trash2,
  ChevronUp, ChevronDown, History, Plus, Pencil, ArrowLeftRight, Share2
} from 'lucide-react';
import client from '../../api/client';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import UserProfileView from '../UserProfileView';
import { RoutineEditor } from './RoutineEditor';
import FastingProtocolCard from '../FastingProtocolCard';
import RoutineShareMenu from '../RoutineShareMenu';
import PersonalRoutinesPreview from './PersonalRoutinesPreview';

export function ClientDetail({ clientDetails, onBack, onRoutine, onDiet, onSupplements, onRefresh }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user: clientUser, recent_workouts, recent_food_logs, recent_measurements, coach_notes: initialNotes, stats, diet_suggestions: initialDietSuggestions } = clientDetails;
  const [notes, setNotes] = useState(initialNotes || []);
  const [newNote, setNewNote] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  const { data: clientRoutine } = useQuery({
    queryKey: ['client-routine', clientUser.id],
    queryFn: async () => {
      const res = await client.get(`/users/${clientUser.id}/routine`);
      return res.data;
    },
    enabled: !!clientUser.routine_id,
  });

  const { data: routineHistory = [] } = useQuery({
    queryKey: ['client-routine-history', clientUser.id],
    queryFn: async () => {
      const res = await client.get(`/users/${clientUser.id}/routine-history`);
      return res.data || [];
    },
    enabled: !!clientUser.routine_id,
  });

  const saveRoutineMutation = useMutation({
    mutationFn: ({ editedRoutine, sessionId }) => client.put(`/users/${clientUser.id}/routine${sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : ''}`, editedRoutine),
    onSuccess: () => {
      toast.success(t('clientDetail.routineSaved'));
      queryClient.invalidateQueries({ queryKey: ['client-routine', clientUser.id] });
      queryClient.invalidateQueries({ queryKey: ['client-routine-history', clientUser.id] });
    },
    onError: () => { toast.error(t('clientDetail.routineSaveFailed')); },
  });

  const saveRoutine = (editedRoutine, { sessionId } = {}) => saveRoutineMutation.mutateAsync({ editedRoutine, sessionId });

  const saveAsTemplateMutation = useMutation({
    mutationFn: () => {
      const routineId = clientRoutine?.id || clientUser.routine_id;
      return client.post(`/routines/${routineId}/save-as-template`);
    },
    onSuccess: () => { toast.success(t('clientDetail.savedAsTemplate')); },
    onError: () => { toast.error(t('clientDetail.saveAsTemplateFailed')); },
  });

  const saveAsTemplate = () => saveAsTemplateMutation.mutate();

  const addNoteMutation = useMutation({
    mutationFn: (note) => client.post(`/users/${clientUser.id}/notes`, { note }),
    onSuccess: (res) => {
      setNotes(prev => [res.data, ...prev]);
      setNewNote('');
      toast.success(t('clientDetail.noteAdded'));
    },
    onError: () => { toast.error(t('clientDetail.noteAddFailed')); },
  });

  const addNote = () => { if (!newNote.trim()) return; addNoteMutation.mutate(newNote.trim()); };

  const deleteNoteMutation = useMutation({
    mutationFn: (noteId) => client.delete(`/users/${clientUser.id}/notes/${noteId}`),
    onSuccess: (_, noteId) => { setNotes(prev => prev.filter(n => n.id !== noteId)); },
    onError: () => { toast.error(t('clientDetail.noteDeleteFailed')); },
  });

  const deleteNote = (noteId) => deleteNoteMutation.mutate(noteId);

  return (
    <UserProfileView
      userDetails={clientDetails}
      onBack={onBack}
      actionButtons={
        <>
          <Button variant="outline" size="sm" className="text-xs" onClick={() => onRoutine(clientUser)} data-testid="change-routine-btn">
            <Dumbbell className="mr-1 h-3.5 w-3.5" /> {clientUser.routine_name ? t('clients.change') : t('clients.assign')}
          </Button>
          {clientUser?.routine_id && clientRoutine && (
            <RoutineShareMenu routine={clientRoutine}>
              <Button variant="ghost" size="icon" className="h-9 w-9" tooltip={t('tooltips.share', 'Share')} data-testid="client-detail-share-btn">
                <Share2 className="h-4 w-4" />
              </Button>
            </RoutineShareMenu>
          )}
          <Button variant="outline" size="sm" className="text-xs" onClick={() => onDiet(clientUser)} data-testid="change-diet-btn">
            <Apple className="mr-1 h-3.5 w-3.5" /> {t('diet.title')}
          </Button>
          <Button variant="outline" size="sm" className="text-xs" onClick={() => onSupplements(clientUser)} data-testid="change-supplements-btn">
            <Pill className="mr-1 h-3.5 w-3.5" /> {t('clientDetail.supplements')}
          </Button>
          <Button variant="outline" size="sm" className="text-xs" onClick={() => navigate(`/clients/${clientUser.id}/analytics`)} data-testid="view-analytics-btn">
            <BarChart3 className="mr-1 h-3.5 w-3.5" /> {t('clientDetail.analytics')}
          </Button>
          <Button size="sm" className="bg-primary hover:bg-primary/90 text-white text-xs" onClick={() => navigate(`/workout?on_behalf_of=${clientUser.id}&client_name=${encodeURIComponent(clientUser.name)}`)} data-testid="log-workout-behalf-btn">
            <Dumbbell className="mr-1 h-3.5 w-3.5" /> {t('clientDetail.logWorkout')}
          </Button>
          <Button size="sm" className="bg-primary hover:bg-primary/90 text-white text-xs" onClick={() => navigate(`/diet?on_behalf_of=${clientUser.id}&client_name=${encodeURIComponent(clientUser.name)}`)} data-testid="log-diet-behalf-btn">
            <Apple className="mr-1 h-3.5 w-3.5" /> {t('clientDetail.logDiet')}
          </Button>
          <Button size="sm" className="bg-primary hover:bg-primary/90 text-white text-xs" onClick={() => navigate(`/measurements?on_behalf_of=${clientUser.id}&client_name=${encodeURIComponent(clientUser.name)}`)} data-testid="log-measurements-behalf-btn">
            <Scale className="mr-1 h-3.5 w-3.5" /> {t('clientDetail.logCheckIn')}
          </Button>
          <Button size="sm" className="bg-primary hover:bg-primary/90 text-white text-xs" onClick={() => navigate(`/supplements?on_behalf_of=${clientUser.id}&client_name=${encodeURIComponent(clientUser.name)}`)} data-testid="log-supplements-behalf-btn">
            <Pill className="mr-1 h-3.5 w-3.5" /> {t('clientDetail.logSupplements')}
          </Button>
        </>
      }
    >
      {/* Client's Routine — Clean Editor */}
      {clientRoutine && (
        <Card className="border-border/50">
          <CardHeader className="pb-3">
            <CardTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {t('clientDetail.clientRoutineTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <RoutineEditor
              routine={clientRoutine}
              clientUser={clientUser}
              onSave={saveRoutine}
              onSaveAsTemplate={saveAsTemplate}
            />
          </CardContent>
        </Card>
      )}

      {/* Routine Change History */}
      {clientUser.routine_id && (
        <Card className="border-border/50">
          <CardHeader className="cursor-pointer" onClick={() => setShowHistory(!showHistory)} data-testid="routine-history-toggle">
            <CardTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }} className="flex items-center justify-between">
              <span>
                <History className="inline h-5 w-5 mr-2 text-primary" /> {t('clientDetail.routineChangeHistoryTitle')}
              </span>
              <span className="flex items-center gap-2">
                {routineHistory.length > 0 && (
                  <Badge variant="secondary" className="text-xs">{routineHistory.length} {routineHistory.length === 1 ? t('clientDetail.change') : t('clientDetail.changes')}</Badge>
                )}
                {showHistory ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </span>
            </CardTitle>
          </CardHeader>
          {showHistory && (
            <CardContent data-testid="routine-history-content">
              {routineHistory.length === 0 ? (
                <p className="text-muted-foreground text-center py-4 text-sm">{t('clientDetail.noRoutineChanges')}</p>
              ) : (
                <ScrollArea className="max-h-[min(400px,50dvh)]">
                  <div className="space-y-4">
                    {routineHistory.map((entry) => (
                      <div key={entry.id} className="border rounded-lg p-3 bg-muted/30" data-testid={`history-entry-${entry.id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">{entry.coach_name}</span>
                          <span className="text-xs text-muted-foreground">{format(new Date(entry.created_at), 'MMM d, yyyy HH:mm')}</span>
                        </div>
                        <div className="space-y-1">
                          {entry.changes.map((change, idx) => {
                            const icons = {
                              day_added: <Plus className="h-3 w-3 text-green-500" />,
                              day_removed: <Trash2 className="h-3 w-3 text-red-500" />,
                              exercise_added: <Plus className="h-3 w-3 text-green-500" />,
                              exercise_removed: <Trash2 className="h-3 w-3 text-red-500" />,
                              sets_changed: <ArrowLeftRight className="h-3 w-3 text-blue-500" />,
                              set_modified: <Pencil className="h-3 w-3 text-amber-500" />,
                              renamed: <Pencil className="h-3 w-3 text-purple-500" />,
                              created: <Plus className="h-3 w-3 text-green-500" />,
                            };
                            return (
                              <div key={idx} className="flex items-start gap-2 text-xs text-muted-foreground">
                                <span className="mt-0.5 flex-shrink-0">{icons[change.type] || <Pencil className="h-3 w-3" />}</span>
                                <span>{change.description}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* User's Own (Personal) Routines — read-only preview */}
      <PersonalRoutinesPreview userId={clientUser.id} userName={clientUser.name} />

      {/* Fasting Protocol */}
      <FastingProtocolCard client={clientUser} onUpdated={onRefresh} />

      {/* Coach Notes */}
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <MessageSquare className="inline h-5 w-5 mr-2 text-primary" /> {t('clientDetail.coachNotesTitle')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-4">
            <Textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder={t('clientDetail.noteplaceholder')} className="min-h-[60px] resize-none" data-testid="coach-note-input" />
            <Button onClick={addNote} className="bg-primary hover:bg-primary/90 self-end" disabled={!newNote.trim()} data-testid="add-note-btn">
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <div className="space-y-3">
            {notes.length === 0 ? (
              <p className="text-muted-foreground text-center py-4 text-sm">{t('clientDetail.noNotes')}</p>
            ) : notes.map((note) => (
              <div key={note.id} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 group">
                <MessageSquare className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm whitespace-pre-wrap break-words">{note.note}</p>
                  <p className="text-xs text-muted-foreground mt-1">{note.coach_name} - {format(new Date(note.created_at), 'MMM d, yyyy HH:mm')}</p>
                </div>
                <Button variant="ghost" size="icon" className="h-9 w-9 md:h-6 md:w-6 opacity-0 group-hover:opacity-100" tooltip={t('tooltips.deleteNote')} onClick={() => deleteNote(note.id)}>
                  <Trash2 className="h-3 w-3 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </UserProfileView>
  );
}

export default ClientDetail;
